// «Удержание» звонка: пока рекрутёр занят (смотрит карточку, ищет документ),
// собеседник слышит музыку, а не тишину и не рекрутёра.
//
// Как устроено — и почему не SIP-удержание. Re-INVITE sendonly остановил бы
// RTP от нас, а у SIP-пользователя 101 на АТС стоит rtp_timeout=30: без потока
// звука линия падает через полминуты, музыки на удержании в диалплане нет, и
// править её через Apply Config мы не можем. Поэтому удержание — целиком на
// стороне браузера: в RTP-отправитель вместо микрофона встаёт поток музыки
// (Web Audio → MediaStreamDestination → RTCRtpSender.replaceTrack), без
// переговоров по SDP. Пакеты идут непрерывно, линия (в том числе через
// GSM-шлюз) не рвётся, запись разговора на АТС пишет музыку как часть линии.
//
// Состояния (чистая часть) и ядро входа/выхода на «ручках» отправителя — без
// React и без браузера, они проверяются тестом; Web Audio — createHoldMedia.

/** Музыка — статика сайта по постоянному адресу (apps/newats-frontend/public/
 * hold/). Файла, пока заказчик его не положил, нет — тогда вместо музыки играет
 * тихий синтезированный тон (HoldSource «tone»). Сменить композицию — положить
 * файл с тем же именем: код не меняется. */
export const HOLD_MUSIC_URL = "/hold/hold-music.mp3";

/** Через сколько удержания окно мягко напоминает, что собеседник ждёт. */
export const HOLD_REMINDER_MS = 2 * 60 * 1000;

/** Плавное начало: без щелчка на входе в удержание и при зацикливании. */
export const HOLD_FADE_IN_S = 0.6;
const MUSIC_GAIN = 0.8;
// Запасной тон — едва слышный: он только держит поток живым.
const TONE_GAIN = 0.012;
const TONE_HZ = [440, 554.37];

// ---- состояния «удержание» и «Без звука» ----

export interface HoldState {
  /** Линия на удержании. */
  held: boolean;
  /** Когда удержание началось (для напоминания); null — не на удержании. */
  since: number | null;
  /** Рекрутёр выключил микрофон кнопкой «Без звука» (до удержания или нет). */
  micMuted: boolean;
}

export const idleHold: HoldState = { held: false, since: null, micMuted: false };

export type HoldAction =
  | { type: "hold"; now: number }
  | { type: "resume" }
  | { type: "toggle_mic" }
  // Линия кончилась или началась новая: звонок начинается с включённым микрофоном.
  | { type: "reset" };

/** Переходы. Удержание глушит микрофон принудительно, но `micMuted` не
 * трогает: то, что рекрутёр выбрал кнопкой «Без звука», возвращается как было
 * (был без звука — остаётся без звука, не размьючиваем насильно). На
 * удержании «Без звука» не переключается — микрофон и так выключен. Тот же
 * объект — «ничего не изменилось». */
export function stepHold(s: HoldState, a: HoldAction): HoldState {
  switch (a.type) {
    case "hold":
      return s.held ? s : { ...s, held: true, since: a.now };
    case "resume":
      return s.held ? { ...s, held: false, since: null } : s;
    case "toggle_mic":
      return s.held ? s : { ...s, micMuted: !s.micMuted };
    case "reset":
      return s.held || s.since !== null || s.micMuted ? idleHold : s;
  }
}

/** Идёт ли с микрофона звук собеседнику: ни удержания, ни «Без звука». */
export function micLive(s: HoldState): boolean {
  return !s.held && !s.micMuted;
}

/** Строка состояния разговора: «Разговор · 03:25», «На удержании · 03:25»,
 * «Разговор · 03:25 · без звука». Таймер удержанием не останавливается. */
export function inCallLabel(clock: string, o: { held?: boolean; muted?: boolean }): string {
  if (o.held) return `На удержании · ${clock}`;
  return o.muted ? `Разговор · ${clock} · без звука` : `Разговор · ${clock}`;
}

function minutesWord(n: number): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return "минуту";
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return "минуты";
  return "минут";
}

/** Мягкое напоминание в окне (текстом, без звука): null — рано или не на
 * удержании. */
export function holdReminder(
  since: number | null,
  now: number,
  afterMs: number = HOLD_REMINDER_MS,
): string | null {
  if (since === null) return null;
  const held = now - since;
  if (held < afterMs) return null;
  const min = Math.max(1, Math.floor(held / 60000));
  return `Собеседник ждёт на линии уже ${min} ${minutesWord(min)}. Вернитесь к разговору или завершите звонок.`;
}

// ---- источник звука ----

export type HoldSource<B> = { kind: "music"; buffer: B } | { kind: "tone" };

export interface HoldFile {
  ok: boolean;
  contentType: string;
  bytes(): Promise<ArrayBuffer>;
}

/** Похоже ли на аудиофайл по заголовку. Файла может не быть, а nginx сайта на
 * любой незнакомый адрес отвечает index.html со статусом 200 — «ok» одного
 * мало. */
export function isAudioContentType(type: string): boolean {
  const t = type.toLowerCase().split(";")[0].trim();
  return t.startsWith("audio/") || t === "application/ogg" || t === "application/octet-stream";
}

/** Выбор источника: музыка, если файл есть и декодируется, иначе тон. Любой
 * отказ — тон: удержание должно состояться при любых обстоятельствах. */
export async function loadHoldSource<B>(d: {
  fetchFile: () => Promise<HoldFile>;
  decode: (bytes: ArrayBuffer) => Promise<B>;
}): Promise<HoldSource<B>> {
  try {
    const file = await d.fetchFile();
    if (!file.ok || !isAudioContentType(file.contentType)) return { kind: "tone" };
    const bytes = await file.bytes();
    if (bytes.byteLength === 0) return { kind: "tone" };
    return { kind: "music", buffer: await d.decode(bytes) };
  } catch {
    return { kind: "tone" };
  }
}

// Байты файла — на всю вкладку: следующее удержание не качает его заново.
// Декодирует каждый раз заново: decodeAudioData отбирает буфер у вызвавшего, а
// результат привязан к частоте контекста.
let fileBytes: ArrayBuffer | null = null;
let filePending: Promise<ArrayBuffer | null> | null = null;

function fetchHoldBytes(): Promise<ArrayBuffer | null> {
  if (fileBytes) return Promise.resolve(fileBytes);
  if (filePending) return filePending;
  const p = (async (): Promise<ArrayBuffer | null> => {
    try {
      // Режим кэша — по умолчанию (nginx сайта отдаёт no-cache, браузер
      // перепроверяет и получает дешёвый 304). force-cache нельзя: пока файла
      // нет, на этот адрес приходит index.html со статусом 200, браузер
      // сохранил бы его под адресом музыки и после выкладки файла отдавал бы
      // страницу вместо звука; то же при замене композиции под тем же именем.
      const res = await fetch(HOLD_MUSIC_URL);
      if (!res.ok || !isAudioContentType(res.headers.get("content-type") ?? "")) return null;
      const bytes = await res.arrayBuffer();
      if (bytes.byteLength === 0) return null;
      fileBytes = bytes;
      return bytes;
    } catch {
      return null;
    }
  })();
  filePending = p;
  void p.finally(() => {
    if (filePending === p) filePending = null;
  });
  return p;
}

/** Подкачать музыку заранее (линия поднята) — к нажатию «Удержание» она уже
 * в памяти. Безвредно, если файла ещё нет. */
export function warmHoldMusic(): void {
  void fetchHoldBytes();
}

// ---- Web Audio: поток для RTP-отправителя ----

export interface HoldMedia {
  /** Дорожка, которая встаёт в отправитель вместо микрофона. */
  track: MediaStreamTrack;
  /** Источник запущен (музыка или запасной тон); не отклоняется. */
  ready: Promise<"music" | "tone">;
  stop(): void;
}

/** Собрать поток удержания. Зовётся прямо из нажатия — AudioContext без жеста
 * браузер не запустит. null — Web Audio нет: удержание невозможно, микрофон
 * не тронут. */
export function createHoldMedia(): HoldMedia | null {
  const AC: typeof AudioContext | undefined =
    typeof window === "undefined"
      ? undefined
      : window.AudioContext ||
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AC) return null;
  let ctx: AudioContext;
  try {
    ctx = new AC();
  } catch {
    return null;
  }
  const dest = ctx.createMediaStreamDestination();
  const track = dest.stream.getAudioTracks()[0];
  if (!track) {
    void ctx.close().catch(() => undefined);
    return null;
  }
  void ctx.resume().catch(() => undefined);

  const gain = ctx.createGain();
  gain.gain.value = 0;
  gain.connect(dest);
  const stoppers: Array<() => void> = [];
  let stopped = false;

  const fadeIn = (target: number) => {
    const t = ctx.currentTime;
    gain.gain.cancelScheduledValues(t);
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(target, t + HOLD_FADE_IN_S);
  };

  const playTone = () => {
    // Два мягких тона и медленное «дыхание» громкости: тихо, но поток живой.
    const breath = ctx.createGain();
    breath.gain.value = TONE_GAIN;
    breath.connect(gain);
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.2;
    const lfoDepth = ctx.createGain();
    lfoDepth.gain.value = TONE_GAIN * 0.8;
    lfo.connect(lfoDepth);
    lfoDepth.connect(breath.gain);
    const oscs = TONE_HZ.map((hz) => {
      const o = ctx.createOscillator();
      o.type = "sine";
      o.frequency.value = hz;
      o.connect(breath);
      return o;
    });
    lfo.start();
    oscs.forEach((o) => o.start());
    stoppers.push(() => {
      for (const o of [lfo, ...oscs]) {
        try {
          o.stop();
        } catch {
          /* уже остановлен */
        }
      }
    });
    fadeIn(1);
  };

  const ready = (async (): Promise<"music" | "tone"> => {
    const src = await loadHoldSource<AudioBuffer>({
      fetchFile: async () => {
        const bytes = await fetchHoldBytes();
        return {
          ok: bytes !== null,
          contentType: "audio/*",
          // decodeAudioData отбирает буфер — отдаём копию, кэш остаётся целым.
          bytes: async () => (bytes as ArrayBuffer).slice(0),
        };
      },
      decode: (bytes) => ctx.decodeAudioData(bytes),
    });
    if (stopped) return src.kind;
    try {
      if (src.kind === "music") {
        const node = ctx.createBufferSource();
        node.buffer = src.buffer;
        node.loop = true;
        node.connect(gain);
        node.start();
        stoppers.push(() => {
          try {
            node.stop();
          } catch {
            /* уже остановлен */
          }
        });
        fadeIn(MUSIC_GAIN);
        return "music";
      }
    } catch {
      /* ниже — тон */
    }
    playTone();
    return "tone";
  })().catch(() => {
    // Что бы ни случилось — поток не должен остаться немым.
    try {
      if (!stopped) playTone();
    } catch {
      /* тишина в отправителе: RTP всё равно идёт */
    }
    return "tone" as const;
  });

  return {
    track,
    ready,
    stop() {
      if (stopped) return;
      stopped = true;
      for (const s of stoppers) s();
      try {
        track.stop();
      } catch {
        /* ignore */
      }
      void ctx.close().catch(() => undefined);
    },
  };
}

// ---- вход и выход на живой линии ----

type Switchable = { enabled: boolean };

/** Ручки живой линии, которые нужны удержанию. */
export interface HoldLine {
  isEnded(): boolean;
  /** Флаг «без звука» у JsSIP: сам по себе возвращает дорожкам enabled при
   * каждом re-INVITE, поэтому на удержании он снят (см. enterHold). */
  setFlag(muted: boolean): void;
  senderTracks(): Switchable[];
  localTracks(): Switchable[];
  sender: { readonly track: MediaStreamTrack | null; replaceTrack(t: MediaStreamTrack | null): Promise<void> };
}

/** Идущее удержание: прежний микрофон (вернётся на выходе) и поток музыки. */
export interface HoldSession {
  mic: MediaStreamTrack;
  media: HoldMedia;
}

function setTracks(line: HoldLine, enabled: boolean) {
  for (const t of line.senderTracks()) t.enabled = enabled;
  for (const t of line.localTracks()) t.enabled = enabled;
}

/** Уйти на удержание.
 *
 * 1. Микрофон выключается сразу — раньше, чем загрузится музыка: пока она
 *    готовится, собеседник слышит тишину, а не рекрутёра. RTP при этом идёт.
 * 2. Музыка запускается, дорожка встаёт в отправитель (replaceTrack).
 * 3. Флаг JsSIP снимается: он, поднятый, выключал бы уже дорожку музыки при
 *    первом же re-INVITE (session timers). Прежний микрофон в отправителе
 *    больше нет — он выключен и никуда не уходит.
 *
 * null — в отправителе ничего не поменялось (музыку не удалось поставить,
 * линия кончилась); микрофон, возможно, оставлен выключенным — вызывающий
 * применяет «Без звука» заново. `isCurrent` — линия всё ещё та же. */
export async function enterHold(
  line: HoldLine,
  media: HoldMedia,
  isCurrent: () => boolean,
): Promise<HoldSession | null> {
  const mic = line.sender.track;
  if (line.isEnded() || !mic) {
    media.stop();
    return null;
  }
  setTracks(line, false);
  await media.ready;
  if (!isCurrent() || line.isEnded()) {
    media.stop();
    return null;
  }
  try {
    await line.sender.replaceTrack(media.track);
  } catch {
    media.stop();
    return null;
  }
  if (!isCurrent() || line.isEnded()) {
    media.stop();
    return null;
  }
  line.setFlag(false);
  return { mic, media };
}

/** Вернуться к разговору: микрофон — в отправитель, а «Без звука» — ровно в то
 * состояние, что было до удержания. false — вернуть микрофон не удалось
 * (остаёмся на удержании). Линия кончилась — только прибрать поток. */
export async function leaveHold(
  line: HoldLine,
  hold: HoldSession,
  micMuted: boolean,
): Promise<boolean> {
  if (line.isEnded()) {
    hold.media.stop();
    return true;
  }
  try {
    await line.sender.replaceTrack(hold.mic);
  } catch {
    return false;
  }
  hold.media.stop();
  // Сначала флаг (он сам раскладывает enabled по дорожкам при re-INVITE), потом
  // сами дорожки — как у setLineMuted.
  line.setFlag(micMuted);
  setTracks(line, !micMuted);
  return true;
}
